/**
 * CPT selections on the current NoteVersion.
 * Terminology comes from CPT Master; persistence is NoteVersion.cpt[] only.
 * Does not write Encounter, reasonCode, PMH, or Problem List.
 */
import { CPT_SYSTEM } from "@/lib/cptApi";
import { createOriginalDraft, getNote, updateDraftVersion } from "@/lib/noteApi";

export function toCptSnapshot(item) {
  const code = String(item?.code || "").trim();
  const display = String(item?.display || item?.description || "").trim();
  if (!code || !display) return null;
  return {
    system: item.system || CPT_SYSTEM,
    code,
    display,
  };
}

export function cptKey(item) {
  const snapshot = toCptSnapshot(item);
  if (!snapshot) return "";
  return `${snapshot.system}|${snapshot.code}`;
}

export function toNoteVersionCpt(items = []) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const snapshot = toCptSnapshot(item);
    if (!snapshot) continue;
    const key = cptKey(snapshot);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(snapshot);
  }
  return out;
}

export function fromNoteVersionCpt(items = []) {
  return toNoteVersionCpt(items);
}

export function addNoteCpt(items, item) {
  const snapshot = toCptSnapshot(item);
  if (!snapshot) return { items: items || [], added: false };
  if ((items || []).some((existing) => cptKey(existing) === cptKey(snapshot))) {
    return { items: items || [], added: false };
  }
  return { items: [...(items || []), snapshot], added: true };
}

export function removeNoteCpt(items, code, system = CPT_SYSTEM) {
  const key = `${system}|${String(code || "").trim()}`;
  return (items || []).filter((item) => cptKey(item) !== key);
}

export function selectedCptKeys(items = []) {
  return (items || []).map((item) => cptKey(item)).filter(Boolean);
}

export async function persistNoteCpt(noteId, items) {
  if (!noteId) {
    return { skipped: true, reason: "missing-note" };
  }
  const cpt = toNoteVersionCpt(items);
  const note = await getNote(noteId);
  const version = note.currentVersion;
  if (version?.status === "draft" && version.id) {
    const data = await updateDraftVersion(noteId, version.id, { cpt });
    return { noteId, versionId: data.id || version.id, updated: true, cpt: data.cpt };
  }
  if (!version) {
    const data = await createOriginalDraft(noteId, { cpt });
    return { noteId, versionId: data.id, created: true, cpt: data.cpt };
  }
  return { skipped: true, reason: "signed", noteId, versionId: version.id };
}
